import { api, IS_PROD } from '../config/api';
import { mockOverview, mockVisitors, mockSettings, mockSaveDomain, mockDemographics } from './embedMock';

// ---- Fit-check embed insights (embed.outfino.io) ----
//
// The brand-facing side of the fit-check button's analytics. Contract
// (Outfino/api feature/embed-analytics, brand-scoped):
//
//   GET /brands/:id/embed/overview?from&to                     viewAnalytics
//     → { totals: { impressions, clicks, signups, logins, matchViews,
//                   uploadSessions, photosUploaded, uniqueVisitors,
//                   addToCarts, addToCartsMatched, purchases, purchasesMatched,
//                   revenueMatched: [{ currency, amount }], follows },
//         rates: {…}, daily: [{ date, impressions, clicks, signups, logins,
//                   matchViews, photosUploaded, addToCarts, purchases, follows }],
//         topProducts: [{ shopifyProductId, handle, title, imageUrl,
//                   impressions, clicks, signups, matchViews, photosUploaded,
//                   avgCompatibility, addToCarts, addToCartsMatched, purchases,
//                   purchasesMatched, revenueMatched: [{ currency, amount }],
//                   follows }] }
//   GET /brands/:id/embed/demographics?from&to                   viewAnalytics
//     → KYC: the AGGREGATE distribution of who matched/viewed the products.
//       k-anonymous, so it is served regardless of the visitor-identity flag.
//       { minBucket, total: number|null, enoughData: bool,
//         ageBands: { bands: [{ key: '18-24'|'25-34'|'35-44'|'45+'|'unknown',
//                       count }], masked: null | { count: number|null, buckets } },
//         gender:  { bands: [{ key: 'FEMALE'|'MALE'|'unknown', count }], masked },
//         styles:  { bands: [{ key: <style axis>|'unknown', count }], masked } }
//       total / every breakdown is null / empty when fewer than `minBucket`
//       viewers ("Még kevés az adat"); a band under `minBucket` users is folded
//       into `masked` (its own count null while that is itself under minBucket),
//       so no visible number describes a group small enough to identify anyone.
//       A whole breakdown absent = an older API without this endpoint (the page
//       feature-detects and shows the test data / nothing).
//   GET /brands/:id/embed/visitors?from&to&productId&event&cursor viewAnalytics
//     → { items: [{ id, isGuest,
//                   user: null | { id, username, displayName, avatarUrl|null,
//                     matches: [{ productId, title, imageUrl, percent, basis:
//                       'percentile'|'absolute', reasons: [{ kind: 'similar_to'
//                       |'pairs_with', label }]|null, sentence: string|null,
//                       lang, matchedAt }] },
//                   match?: null | { percent, basis, reasons, sentence, lang, matchedAt },
//                   — `sentence` is the exact text the shopper saw under the
//                     match % (null if they never opened it); older API omits it
//                   — user / match only under the API's visitor identity flag
//                     (EMBED_VISITOR_IDENTITY_ENABLED; off in production until
//                     the privacy policy update): off, user is null and match
//                     is absent, and the page shows "Látogató" / "Vendég"
//                   event: 'signup'|'login'|'match_view'|'upload'
//                          |'add_to_cart'|'purchase'|'follow',
//                   product: { shopifyProductId, handle, title, imageUrl },
//                   photosUploaded, compatibility, occurredAt,
//                   matched?, quantity?, orderValue?: { amount, currency } }],
//         nextCursor }   — purchases never carry the order id or customer data
//   GET /brands/:id/embed/settings                               editBrand
//     → { key, allowedDomains, status: 'configured'|'not_configured',
//         lastSeenAt, seenDomains: [{ domain, lastSeenAt, impressions7d }],
//         domain: 'nova-atelier.hu' | null,   — the one webshop the key runs on
//         tracking: { pixelStatus: 'configured'|'not_configured',
//                     lastAddToCartAt, lastPurchaseAt,
//                     platformsSeen: ['shopify'|'woocommerce'|'custom'] } }
//
// "Matched" (carts, purchases, revenue): the same visitor viewed the product
// matching for that product within the 30 days before.
//
// `from` / `to` are local calendar days, YYYY-MM-DD, both inclusive.
//
// TEST DATA (api/embedMock.js) stands in until those endpoints are deployed:
//   • REACT_APP_EMBED_MOCK=1 → always the test data, in any build;
//   • otherwise, outside production builds, when the endpoint answers 404.
// A production build never shows it unless the flag is set. Every loader
// resolves to { data, mock } so the page can label test data as such.

const FORCE_MOCK = process.env.REACT_APP_EMBED_MOCK === '1';

export function embedMockAllowed({ force = FORCE_MOCK, prod = IS_PROD } = {}) {
  return force || !prod;
}

export async function withEmbedMock(load, mock, { force = FORCE_MOCK, prod = IS_PROD } = {}) {
  if (force) return { data: await mock(), mock: true };
  try {
    return { data: await load(), mock: false };
  } catch (err) {
    if (err?.status === 404 && embedMockAllowed({ force, prod })) return { data: await mock(), mock: true };
    throw err;
  }
}

// A beat of latency for the test data, so loading states look like they will.
export const later = (fn) => () => new Promise((resolve) => setTimeout(() => resolve(fn()), 220));

export function query(params) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : '';
}

export function fetchEmbedOverview(brandId, { from, to } = {}) {
  return withEmbedMock(
    () => api(`/brands/${brandId}/embed/overview${query({ from, to })}`),
    later(() => mockOverview(brandId, { from, to }))
  );
}

// KYC demographics (viewAnalytics). Aggregate only; the API serves it with or
// without the visitor-identity flag. The page feature-detects a null/absent
// body or missing breakdowns and shows the test data / nothing accordingly.
export function fetchEmbedDemographics(brandId, { from, to } = {}) {
  return withEmbedMock(
    () => api(`/brands/${brandId}/embed/demographics${query({ from, to })}`),
    later(() => mockDemographics(brandId, { from, to }))
  );
}

export function fetchEmbedVisitors(brandId, { from, to, productId, event, cursor } = {}) {
  return withEmbedMock(
    () => api(`/brands/${brandId}/embed/visitors${query({ from, to, productId, event, cursor })}`),
    later(() => mockVisitors(brandId, { from, to, productId, event, cursor }))
  );
}

// `mockStatus` only steers the test data (to see the "not set up" state).
export function fetchEmbedSettings(brandId, { mockStatus } = {}) {
  return withEmbedMock(
    () => api(`/brands/${brandId}/embed/settings`),
    later(() => mockSettings(brandId, { status: mockStatus }))
  );
}

/// Saves the webshop address (editBrand): PUT /brands/:id/embed/settings
/// { domain } → the settings, with `domain`. Always the API first; the test
/// data takes it when the endpoint 404s outside production builds.
export function saveEmbedDomain(brandId, domain, { mockStatus } = {}) {
  return withEmbedMock(
    () => api(`/brands/${brandId}/embed/settings`, { method: 'PUT', body: { domain } }),
    later(() => mockSaveDomain(brandId, domain, { status: mockStatus }))
  );
}

// ---- Diagnostics: the "Teszt" button ----
//
// GET /brands/:id/embed/diagnostics (editBrand OR admin) →
//   { ok, key, domain, checks: { keyValid, domainSet, brandActive,
//     impressionSeen, eventsSeen, pixelSeen } (each { pass, lastSeenAt? }),
//     foreignDomain, lastErrorCode }.
//
// The endpoint is new; a prod API that predates it answers 404. We FEATURE
// DETECT: on 404 (and not in a build that forces the test data) we fall back
// to GET /embed/settings and SYNTHESISE the same shape from what it already
// gives us — status 'configured' (the ground truth), the key, the domain, the
// pixel status, the seen domains. Every build keeps working against the
// current prod API, and the richer per-check view lights up the moment the
// endpoint ships.

/// settings → a diagnostics-shaped object (the fallback, and the test data).
export function diagnosticsFromSettings(settings) {
  const s = settings || {};
  const configured = s.status === 'configured';
  const keyValid = typeof s.key === 'string' && /^ofk_(live|test)_[A-Za-z0-9]{32}$/.test(s.key);
  const domainSet = !!s.domain;
  const pixel = s.tracking || {};
  const pixelSeen = ['configured', 'active'].includes(pixel.pixelStatus);
  // the one explanation we can derive without the endpoint
  const lastErrorCode = !keyValid ? 'KEY_NOT_FOUND' : !domainSet ? 'DOMAIN_NOT_SET' : configured ? null : 'NO_IMPRESSIONS';
  return {
    ok: configured,
    degraded: true, // from settings, not the full diagnostics endpoint
    key: s.key ?? null,
    domain: s.domain ?? null,
    checks: {
      keyValid: { pass: keyValid },
      domainSet: { pass: domainSet },
      // the old API cannot tell these apart from "configured"; mirror ok
      brandActive: { pass: keyValid && domainSet },
      impressionSeen: { pass: configured, lastSeenAt: s.lastSeenAt ?? null },
      eventsSeen: { pass: configured, lastSeenAt: configured ? s.lastSeenAt ?? null : null },
      pixelSeen: { pass: pixelSeen, lastSeenAt: pixel.lastAddToCartAt || pixel.lastPurchaseAt || null },
    },
    foreignDomain: null,
    lastErrorCode,
  };
}

/// GET diagnostics, with the settings fallback. -> { data, mock, degraded }.
export async function fetchEmbedDiagnostics(brandId, { mockStatus } = {}) {
  if (FORCE_MOCK) {
    const settings = await mockSettings(brandId, { status: mockStatus });
    return { data: diagnosticsFromSettings(settings), mock: true, degraded: true };
  }
  try {
    const data = await api(`/brands/${brandId}/embed/diagnostics`);
    return { data: { degraded: false, ...data }, mock: false, degraded: false };
  } catch (err) {
    // 404: the endpoint is not deployed yet — synthesise from settings.
    if (err?.status === 404) {
      const res = await fetchEmbedSettings(brandId, { mockStatus });
      return { data: diagnosticsFromSettings(res.data), mock: res.mock, degraded: true };
    }
    throw err;
  }
}

// ---- Product crawl: "Termékek fetchelése" ----
//
// POST /brands/:id/products/crawl (editProducts OR admin) → 202 { jobId,
//   status, platform, phase, found, ingested, failed, error }.
// GET  /brands/:id/products/crawl/:jobId → the same, polled to completion.
//
// Feature-detected like the diagnostics: a prod API without it answers 404,
// and the caller surfaces "not available yet" instead of failing loudly.
export class CrawlUnavailable extends Error {
  constructor() { super('crawl endpoint not available'); this.name = 'CrawlUnavailable'; }
}

export async function startProductCrawl(brandId) {
  try {
    return await api(`/brands/${brandId}/products/crawl`, { method: 'POST' });
  } catch (err) {
    // 404: not deployed yet. UnsupportedInEmbedded: not offered in the Shopify
    // admin iframe. Either way, the button shows "coming soon".
    if (err?.status === 404 || err?.name === 'UnsupportedInEmbedded') throw new CrawlUnavailable();
    throw err;
  }
}

export function fetchCrawlStatus(brandId, jobId) {
  return api(`/brands/${brandId}/products/crawl/${jobId}`);
}
